import {create} from "zustand";
import BookMemoResponse from "./memo/BookMemoResponse";
import BookReadingSessionResponse from "../reading/BookReadingSessionResponse";
import BookDetailResponse from "./BookDetailResponse";
import BookResponse from "../BookResponse";
import BookStatus from "./BookStatusType";

interface BookDetailStore {
    book: BookResponse | null
    memos: BookMemoResponse[]
    readingSessions: BookReadingSessionResponse[]

    addMemo: (memo: BookMemoResponse) => void
    editMemo: (memo: BookMemoResponse) => void
    deleteMemo: (memoId: number) => void

    detailMemo: BookMemoResponse | null
    openDetailMemo: (memo: BookMemoResponse) => void
    closeDetailMemo: () => void

    addReadingSession: (readingSession: BookReadingSessionResponse) => void
    editReadingSession: (readingSession: BookReadingSessionResponse) => void
    deleteReadingSession: (readingSession: BookReadingSessionResponse) => void

    updateBookStatus: (status: BookStatus) => void
    updateBook: (updates: Partial<BookResponse>) => void
    updateReview: (review: string | null) => void

    init: (book: BookDetailResponse) => void
}

const useBookDetailStore = create<BookDetailStore>(set => ({
    book: null,
    memos: [],
    readingSessions: [],

    addMemo: (memo) => set(state => ({memos: [...state.memos, memo]})),
    editMemo: (memo) => set(state => (
        {
            memos: state.memos.map(m => m.id === memo.id ? memo : m),
            detailMemo: memo
        }
    )),
    deleteMemo: (memoId) => set(state => ({memos: state.memos.filter(memo => memo.id !== memoId)})),

    detailMemo: null,
    openDetailMemo: (memo) => set({detailMemo: memo}),
    closeDetailMemo: () => set({detailMemo: null}),

    addReadingSession: (readingSession) => set(state => ({
        readingSessions: state.readingSessions.some(rs => rs.id === readingSession.id)
            ? state.readingSessions
            : [...state.readingSessions, readingSession],
        book: state.book
            ? {
                ...state.book,
                currentPage: Math.max(readingSession.endPage ?? 0, state.book.currentPage),
            }
            : null
    })),
    editReadingSession: (readingSession) => {
        set(state => ({
            readingSessions: state.readingSessions.map(rs => rs.id === readingSession.id ? readingSession : rs),
            book: state.book
                ? {
                    ...state.book,
                    currentPage: Math.max(...state.readingSessions.map(rs => rs.id === readingSession.id ? readingSession : rs).map(r => r.endPage ?? 0))
                }
                : null
        }))
    },
    deleteReadingSession: (readingSession) => {
        set(state => ({
            readingSessions: state.readingSessions.filter(r => r.id !== readingSession.id),
            book: state.book
                ? {
                    ...state.book,
                    currentPage: Math.max(...state.readingSessions.filter(r => r.id !== readingSession.id).map(r => r.endPage ?? 0))
                }
                : null
        }))
    },

    updateBookStatus: (status) => set(state => ({
        book: state.book ? {...state.book, status: status} : state.book
    })),
    updateBook: (updates) => set(state => ({
        book: state.book ? {...state.book, ...updates} : state.book
    })),
    updateReview: (review) => set(state => ({
        book: state.book ? {...state.book, review: review} : state.book
    })),

    init: (book) => {
        set({book: book.book})
        set({memos: book.memos})
        set({readingSessions: book.readingSessions})
    }
}))

export default useBookDetailStore
